import * as yup from 'yup';

/**
 * Ottimizzazione di `ObjectSchema._sortErrors` per ordinamento errori.
 *
 * Il problema originale: `sortByKeyOrder` (riga 1599 di yup) crea un comparator
 * che chiama `findIndex()` per ogni coppia di errori durante `Array.sort()`.
 * `findIndex()` scorre TUTTE le chiavi con `String.includes()` ogni volta.
 * Con N errori e K chiavi → O(N log N × K) — molto lento con tanti errori.
 *
 * Questa patch intercetta `setFields()` per sostituire `_sortErrors` con una
 * versione che:
 * 1. Costruisce una Map<key, index> una volta sola (O(K))
 * 2. Cache-a il risultato per path → indice (evita ricalcolo nelle comparazioni)
 *
 * Risultato: O(N × K) per il primo sort, O(N log N) per sort successivi.
 *
 * NOTA: questa patch dipende dalla struttura interna di Yup (@favoloso/yup 1.4.0-fv.4).
 * Se Yup viene aggiornato, verificare che la struttura interna sia compatibile.
 * Il sanity check all'avvio protegge da rotture silenziose.
 */

// Otteniamo il prototype di ObjectSchema
const ObjectSchemaProto = Object.getPrototypeOf(yup.object());

/**
 * Verifica che ObjectSchema abbia la struttura interna attesa dalla patch.
 */
function isObjectSchemaCompatible(): boolean {
  if (typeof ObjectSchemaProto.setFields !== 'function') return false;

  // Verifichiamo che setFields produca un risultato con _sortErrors
  try {
    const testObj = yup.object({});
    if (typeof (testObj as any)._sortErrors !== 'function') return false;
  } catch {
    return false;
  }

  return true;
}

if (isObjectSchemaCompatible()) {
  applyOptimizeSortErrorsPatch();
} else {
  console.warn(
    '[optimizeSortErrors] Struttura interna di ObjectSchema non compatibile. ' +
      'La patch non è stata applicata. Verificare la versione di Yup.',
  );
}

function applyOptimizeSortErrorsPatch() {
  const originalSetFields = ObjectSchemaProto.setFields;

  ObjectSchemaProto.setFields = function (
    shape: Record<string, any>,
    excludedEdges?: any,
  ) {
    const result = originalSetFields.call(this, shape, excludedEdges);

    // Sostituiamo _sortErrors con versione ottimizzata
    const keys = Object.keys(shape);
    result._sortErrors = createOptimizedSortComparator(keys);

    return result;
  };
}

function createOptimizedSortComparator(
  keys: string[],
): (a: { path?: string }, b: { path?: string }) => number {
  // Map chiave → indice: costruita una volta sola
  const keyIndex = new Map<string, number>();
  for (let i = 0; i < keys.length; i++) {
    keyIndex.set(keys[i], i);
  }

  // Cache path → sortIndex: persiste tra le comparazioni
  // Bounded dal numero di path unici (≈ numero di errori unici)
  const pathCache = new Map<string, number>();

  return (a, b) => {
    return (
      getCachedIndex(a.path, keyIndex, pathCache) -
      getCachedIndex(b.path, keyIndex, pathCache)
    );
  };
}

function getCachedIndex(
  path: string | undefined,
  keyIndex: Map<string, number>,
  cache: Map<string, number>,
): number {
  if (!path) return Number.POSITIVE_INFINITY;

  let idx = cache.get(path);
  if (idx !== undefined) return idx;

  idx = Number.POSITIVE_INFINITY;
  for (const [key, i] of keyIndex) {
    if (path.includes(key)) {
      idx = i;
      break;
    }
  }

  cache.set(path, idx);
  return idx;
}
